import type { POI, Vec3 } from '../types';
import { searchPois, getPoiById } from '../services/poiService';
import { getTours, type Tour } from '../services/tourService';
import { getProducts, searchProducts } from '../services/productService';
import { loadConfig } from '../services/configService';
import { dist3, formatMetres } from '../utils/math';
import { t } from '../i18n';
import type { ParkingSpot } from '../services/parkingService';
import { escapeHtml } from '../utils/html';
import { loadPoiTranslations, displayPoi } from '../services/poiI18nService';

const ACCESSIBLE_KEY = 'navme.route.accessibleOnly';
const PROXIMITY_KEY = 'navme.proximity.enabled';

/** Audience scoping (healthcare pack, feature 'audienceModes'). */
export type Audience = 'all' | 'patient' | 'visitor' | 'staff';

/** True when the POI carries a currently-running offer (retail pack). */
function hasActiveOffer(poi: POI): boolean {
  if (!poi.offer) return false;
  if (!poi.offerUntil) return true;
  const until = Date.parse(poi.offerUntil);
  return Number.isNaN(until) || until > Date.now();
}

/**
 * Searchable bottom-sheet destination picker (shown after localization).
 * - Distance-sorted when the user's position is known ("what's closest").
 * - Detail cards: description, media badge, straight-line distance chip.
 * - ♿ step-free toggle (persisted) — read via `accessibleOnly` when routing.
 * - 🏷 offers rail (retail pack, feature 'offers').
 * - 🛒 product results — "Milk → Aisle 4" (retail pack, feature 'productFinder').
 * - Audience scoping via ?aud= (healthcare pack, feature 'audienceModes').
 */
export class DestinationPicker {
  readonly el: HTMLDivElement;
  private list: HTMLDivElement;
  private input: HTMLInputElement;
  private accessToggle: HTMLInputElement;
  private proxToggle!: HTMLInputElement;
  private proxRowEl!: HTMLLabelElement;
  private userPos: Vec3 | null = null;
  private personalSpot: ParkingSpot | null = null;
  private gates = {
    tours: true,
    proximity: true,
    offers: false,
    productFinder: false,
    audienceModes: false,
    accessibleDefault: false,
  };
  /** ?aud= embed param — scopes the catalog when 'audienceModes' is on. */
  private audience: Audience | null = null;
  /** Wired by main: navigate-to-spot and clear-spot actions. */
  onSpotSelected?: () => void;
  onSpotCleared?: () => void;

  constructor(
    private onSelect: (poi: POI) => void,
    private onSelectTour?: (tour: Tour) => void,
  ) {
    this.el = document.createElement('div');
    // picker-sheet: own max-height override (user request 2026-07-18 —
    // "Where to" was taking over half the screen) — see .picker-sheet in
    // styles.css. Kept as a separate class rather than shrinking .sheet
    // itself so other sheets (Mark POI form, concierge chat, etc.) that
    // also use the plain .sheet base class are unaffected.
    this.el.className = 'sheet picker-sheet hidden';
    this.el.innerHTML = `<div class="sheet-handle" title="Hide"></div><h2>${t('picker.whereTo')}</h2>`;
    // Tapping the grab-handle minimizes the sheet (reopen via 🧭). Tapping
    // outside the sheet also hides it — see the pointerdown listener in
    // main.ts — so the AR view is never stuck behind the picker.
    this.el.querySelector('.sheet-handle')!.addEventListener('click', () => this.hide());

    this.input = document.createElement('input');
    this.input.type = 'search';
    this.input.placeholder = t('picker.search');
    this.input.className = 'search-input';
    this.input.addEventListener('input', () => void this.render(this.input.value));

    const accessRow = document.createElement('label');
    accessRow.className = 'sheet-toggle';
    this.accessToggle = document.createElement('input');
    this.accessToggle.type = 'checkbox';
    this.accessToggle.checked = localStorage.getItem(ACCESSIBLE_KEY) === '1';
    this.accessToggle.addEventListener('change', () => {
      localStorage.setItem(ACCESSIBLE_KEY, this.accessToggle.checked ? '1' : '0');
    });
    accessRow.append(this.accessToggle, document.createTextNode(' ' + t('picker.stepFree')));

    const proxRow = document.createElement('label');
    this.proxRowEl = proxRow;
    proxRow.className = 'sheet-toggle';
    this.proxToggle = document.createElement('input');
    this.proxToggle.type = 'checkbox';
    this.proxToggle.checked = localStorage.getItem(PROXIMITY_KEY) === '1';
    this.proxToggle.addEventListener('change', () => {
      localStorage.setItem(PROXIMITY_KEY, this.proxToggle.checked ? '1' : '0');
    });
    proxRow.append(this.proxToggle, document.createTextNode(' ' + t('picker.alerts')));

    this.list = document.createElement('div');
    this.list.className = 'poi-list';

    this.el.append(this.input, accessRow, proxRow, this.list);
    void this.render('');
  }

  /** Wheelchair/step-free routing preference (pass to calculateRoute). */
  get accessibleOnly(): boolean {
    return this.accessToggle.checked;
  }

  /** Opt-in "you're passing X" notifications. */
  get proximityEnabled(): boolean {
    return this.proxToggle.checked;
  }

  /** Super Admin feature gates (per venue). Partial — unmentioned keys keep
   *  their current value, so callers can wire gates incrementally. */
  setFeatureGates(gates: Partial<DestinationPicker['gates']>): void {
    this.gates = { ...this.gates, ...gates };
    this.proxRowEl.classList.toggle('hidden', !this.gates.proximity);
    // 🏥 accessibleDefault: step-free routing starts ON for venues that ask
    // for it (hospitals) — but a visitor's explicit choice always wins.
    if (this.gates.accessibleDefault && localStorage.getItem(ACCESSIBLE_KEY) === null) {
      this.accessToggle.checked = true;
    }
    void this.render(this.input.value);
  }

  /** ?aud= embed param (validated by main.ts). Null = everyone. */
  setAudience(aud: Audience | null): void {
    this.audience = aud;
    void this.render(this.input.value);
  }

  /** Shows/removes the pinned "my parking spot" card. */
  setPersonalSpot(spot: ParkingSpot | null): void {
    this.personalSpot = spot;
    void this.render(this.input.value);
  }

  /** Pass the user's current map-space position to enable distance sorting. */
  show(userPos?: Vec3): void {
    if (userPos) this.userPos = userPos;
    this.el.classList.remove('hidden');
    void this.render(this.input.value);
  }

  hide(): void {
    this.el.classList.add('hidden');
  }

  /** Audience scoping: unset/'all' POIs show for everyone. */
  private audienceAllows(poi: POI): boolean {
    if (!this.gates.audienceModes || !this.audience) return true;
    return !poi.audience || poi.audience === 'all' || poi.audience === this.audience;
  }

  private async render(query: string): Promise<void> {
    await loadPoiTranslations(); // no-op when already loaded / lang=en
    const pois = (await searchPois(query)).filter((p) => this.audienceAllows(p));
    this.list.innerHTML = '';

    // Pinned personal parking spot (device-local, current map only).
    if (this.personalSpot && !query.trim()) {
      const spot = this.personalSpot;
      const item = document.createElement('button');
      item.className = 'poi-item poi-card spot-card';
      const d = this.userPos ? dist3(this.userPos, spot.position) : null;
      item.innerHTML = `
        <span class="poi-icon">🚗</span>
        <span class="poi-text">
          <span class="poi-name">${escapeHtml(t('park.mySpot'))}</span>
        </span>
        ${d !== null ? `<span class="poi-dist">${formatMetres(d)}</span>` : ''}`;
      item.addEventListener('click', () => this.onSpotSelected?.());
      const clear = document.createElement('span');
      clear.className = 'spot-clear';
      clear.textContent = '✕';
      clear.title = 'Forget spot';
      clear.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.onSpotCleared?.();
      });
      item.appendChild(clear);
      this.list.appendChild(item);
    }

    // 🏷 Offers rail (retail pack): horizontally scrollable cards for every
    // POI with a live offer — tap routes straight to it. Hidden mid-search.
    if (this.gates.offers && !query.trim()) {
      const offered = pois.filter(hasActiveOffer);
      if (offered.length > 0) {
        const h = document.createElement('p');
        h.className = 'poi-section-title';
        h.textContent = t('picker.offers');
        this.list.appendChild(h);
        const rail = document.createElement('div');
        rail.className = 'offers-rail';
        for (const poi of offered) {
          const disp = displayPoi(poi);
          const card = document.createElement('button');
          card.className = 'offer-card';
          card.innerHTML = `
            <span class="offer-tag">🏷 ${escapeHtml(poi.offer ?? '')}</span>
            <span class="offer-poi">${poi.icon ?? '📍'} ${escapeHtml(disp.name)}</span>`;
          card.addEventListener('click', () => this.onSelect(poi));
          rail.appendChild(card);
        }
        this.list.appendChild(rail);
      }
    }

    // 🛒 Product results (retail pack): the search box also matches product
    // names/keywords — "milk" finds the aisle even though no POI is called
    // milk. Rendered above the POI matches.
    if (this.gates.productFinder && query.trim().length >= 2) {
      try {
        const matches = searchProducts(await getProducts(loadConfig().code), query);
        const rows = (
          await Promise.all(
            matches.map(async (prod) => ({ prod, poi: await getPoiById(prod.poi_id) })),
          )
        ).filter((r): r is { prod: (typeof matches)[number]; poi: POI } => !!r.poi && this.audienceAllows(r.poi));
        if (rows.length > 0) {
          const h = document.createElement('p');
          h.className = 'poi-section-title';
          h.textContent = t('picker.products');
          this.list.appendChild(h);
          for (const { prod, poi } of rows) {
            const disp = displayPoi(poi);
            const d = this.userPos ? dist3(this.userPos, poi.position) : null;
            const item = document.createElement('button');
            item.className = 'poi-item poi-card product-card';
            item.innerHTML = `
              <span class="poi-icon">🛒</span>
              <span class="poi-text">
                <span class="poi-name">${escapeHtml(prod.name)}</span>
                <span class="poi-meta">→ ${poi.icon ?? '📍'} ${escapeHtml(disp.name)} · Floor ${poi.floor}</span>
              </span>
              ${d !== null ? `<span class="poi-dist">${formatMetres(d)}</span>` : ''}`;
            item.addEventListener('click', () => this.onSelect(poi));
            this.list.appendChild(item);
          }
        }
      } catch {
        /* products unavailable — POI search still works */
      }
    }

    // Guided tours section (hidden while searching; super-gated per venue).
    if (!query.trim() && this.onSelectTour && this.gates.tours) {
      const tours = await getTours();
      if (tours.length > 0) {
        const h = document.createElement('p');
        h.className = 'poi-section-title';
        h.textContent = t('picker.tours');
        this.list.appendChild(h);
        for (const tour of tours) {
          const item = document.createElement('button');
          item.className = 'poi-item poi-card tour-card';
          item.innerHTML = `
            <span class="poi-icon">🚩</span>
            <span class="poi-text">
              <span class="poi-name">${escapeHtml(tour.name)}</span>
              <span class="poi-meta">${t('picker.stops', { n: tour.poiIds.length })}${tour.description ? ` · ${escapeHtml(tour.description)}` : ''}</span>
            </span>`;
          item.addEventListener('click', () => this.onSelectTour?.(tour));
          this.list.appendChild(item);
        }
        const h2 = document.createElement('p');
        h2.className = 'poi-section-title';
        h2.textContent = t('picker.dests');
        this.list.appendChild(h2);
      }
    }

    if (pois.length === 0) {
      this.list.innerHTML += `<p class="muted">${t('picker.none')}</p>`;
      return;
    }

    // Nearest first (straight-line — cheap and good enough for ordering);
    // alphabetical when position is unknown.
    const entries = pois.map((poi) => ({
      poi,
      d: this.userPos ? dist3(this.userPos, poi.position) : null,
    }));
    entries.sort((a, b) =>
      a.d !== null && b.d !== null ? a.d - b.d : a.poi.name.localeCompare(b.poi.name),
    );

    for (const { poi, d } of entries) {
      const badge =
        poi.mediaType === 'image' ? ' 🖼' : poi.mediaType === 'video' ? ' 🎬' : poi.mediaType === 'model' ? ' 🧊' : poi.mediaType === 'link' ? ' 🔗' : poi.mediaType === 'audio' ? ' 🔉' : '';
      const offerBadge = this.gates.offers && hasActiveOffer(poi) ? ' 🏷' : '';
      const item = document.createElement('button');
      item.className = 'poi-item poi-card';
      // Content-agent translations overlay name/description for display.
      const disp = displayPoi(poi);
      item.innerHTML = `
        <span class="poi-icon">${poi.icon ?? '📍'}</span>
        <span class="poi-text">
          <span class="poi-name">${escapeHtml(disp.name)}${badge}${offerBadge}</span>
          <span class="poi-meta">${escapeHtml(poi.category)} · Floor ${poi.floor}</span>
          ${disp.description ? `<span class="poi-desc">${escapeHtml(disp.description)}</span>` : ''}
        </span>
        ${d !== null ? `<span class="poi-dist">${formatMetres(d)}</span>` : ''}`;
      item.addEventListener('click', () => this.onSelect(poi));
      this.list.appendChild(item);
    }
  }
}
